/**
 * What a proposal or contract *says*, decided once, for every renderer of it.
 *
 * Two of them read a document now. `DocumentView` draws it in the DOM, laid
 * out on letter sheets by `paged.ts`, and the PDF is a photograph of exactly
 * that — which is why those two can never disagree. The Word export cannot
 * join that arrangement: the reason anybody asks for a `.docx` is to edit it,
 * and a page image pasted into Word is a PDF with the wrong extension. So it
 * rebuilds the document from data, and the moment a fact, a column or a
 * signature line is added to only one of the two builders, a client's Word
 * copy and their PDF start saying different things.
 *
 * The derivations both builders would otherwise each work out for themselves
 * therefore live here: the heading each kind carries, the facts grid, and a
 * payment block's merged schedule. The rows behind the money tables stay in
 * `pages/contract-parts.tsx` beside the tables that draw them, which is what
 * keeps this module free of any runtime import from there — a cycle would
 * otherwise run through every page that renders a document.
 *
 * What stays in each renderer is only how to *draw* the result, which is
 * genuinely different: one paints a fixed 7.5in column the PDF exporter
 * slices, the other hands Word a document it repaginates itself.
 */
import { type ResolvedProposalSegment, combineSchedules } from "@dantria/core";
import type { SegmentDraft } from "./pages/contract-parts";

/** Copy that differs between the offer and the binding document it converts
 *  into — on the document itself. Page chrome around it (back links, list
 *  headings) is the page's own business and stays there. */
export const DOCUMENT_COPY = {
  proposal: { heading: "Project proposal", numberLabel: "Proposal no." },
  contract: { heading: "Contract agreement", numberLabel: "Contract no." },
} as const;

export type DocumentKind = keyof typeof DOCUMENT_COPY;

/** The header fields a document prints, as both renderers need them. Declared
 *  structurally so the editor's loaded row and the public link's payload —
 *  which carry more besides — both satisfy it without a conversion step. */
export interface DocumentContract {
  number: string;
  title: string;
  currency: string;
  tagline: string | null;
  projectType: string | null;
  projectLocation: string | null;
  preparedBy: string | null;
  clientContactName: string | null;
  clientPhone: string | null;
  clientEmail: string | null;
  issuedOn: string | null;
  validThrough: string | null;
  acceptedByName: string | null;
  acceptedAt: string | null;
}

/**
 * The facts grid under the letterhead, already filtered to what is set.
 *
 * Filtering here rather than in each renderer is not tidiness: an empty label
 * in the DOM collapses to a thin line nobody notices, while an empty row in a
 * Word table is a visible blank band. Deciding once means the two can't
 * disagree about how much of the grid is even there.
 */
export function documentFacts(
  kind: DocumentKind,
  contract: DocumentContract,
  clientName: string,
): [string, string][] {
  const rows: [string, string | null][] = [
    ["Prepared for", contract.clientContactName || clientName],
    ["Project", contract.title],
    ["Project type", contract.projectType],
    ["Location", contract.projectLocation],
    ["Phone", contract.clientPhone],
    ["Email", contract.clientEmail],
    [DOCUMENT_COPY[kind].numberLabel, contract.number],
    ["Prepared by", contract.preparedBy],
    ["Date", contract.issuedOn],
    ["Valid through", contract.validThrough],
  ];
  return rows.filter((r): r is [string, string] => !!r[1]);
}

/**
 * A payment-schedule block's rows, merged across the parts it names.
 *
 * `combineSchedules` on a single segment reproduces that segment's own rows
 * exactly — same total, same per-row amount and share — so there is no
 * separate one-part path to keep in step with the merged one.
 */
export function scheduleRowsFor(
  segmentKeys: readonly number[],
  segments: readonly SegmentDraft[],
  resolvedByKey: ReadonlyMap<number, ResolvedProposalSegment | null>,
) {
  const picked = segments.filter((s) => segmentKeys.includes(s.key));
  if (picked.length === 0) return { picked, combined: null };
  const resolved = picked
    .map((s) => resolvedByKey.get(s.key))
    .filter((r): r is ResolvedProposalSegment => !!r);
  return { picked, combined: combineSchedules(resolved) };
}

/** "Kitchen", "Kitchen & Closet", "Kitchen, Closet & Primary bath" — reads as
 *  a list rather than the punctuation a plain `.join(", ")` would leave on a
 *  merged schedule's heading. */
export function joinPartNames(names: readonly string[]): string {
  const labels = names.map((n) => n || "Untitled");
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} & ${labels[labels.length - 1]}`;
}
